import { Box, Typography } from "@mui/material";
import { ReactNode } from "react";

export type CalloutTone = "warn" | "neutral" | "info";

interface Props {
  tone?: CalloutTone;
  icon?: ReactNode;
  /** Más compacto y centrado verticalmente (padding 10/14, letra 12.5), como los avisos de una línea. */
  compact?: boolean;
  children: ReactNode;
}

/** Tokens de cada tono; son colores fijos del diseño, no varían por tenant. */
const TONE_TOKENS = {
  warn: { bg: "warningSoftBg", border: "warningSoftBorder" },
  neutral: { bg: "neutralSoftBg", border: "neutralSoftBorder" },
} as const;

/**
 * `info`: el `.callout` base del diseño v2. Fondo y borde azul muy claro fijos (`--ld-blue-50/100`); el texto
 * va en el azul marino de la marca (`primary.dark`) y el ícono en su color (`primary.main`).
 */
const InfoCallout = ({ icon, compact, children }: Omit<Props, "tone">) => (
  <Box
    sx={{
      display: "flex",
      alignItems: compact ? "center" : "flex-start",
      gap: 1.5,
      p: compact ? "10px 14px" : "14px 16px",
      bgcolor: "#F4F8FD",
      border: "1px solid #E8F1FB",
      borderRadius: "10px",
      "& > svg": { flex: "none", color: "primary.main" },
    }}
  >
    {icon}
    <Typography
      component="div"
      sx={{
        fontSize: compact ? 12.5 : 13,
        lineHeight: compact ? 1.45 : 1.5,
        color: "primary.dark",
        "& strong": { fontWeight: 600, color: "primary.dark" },
      }}
    >
      {children}
    </Typography>
  </Box>
);

export const Callout = ({ tone = "neutral", icon, compact, children }: Props) => {
  if (tone === "info") {
    return (
      <InfoCallout icon={icon} compact={compact}>
        {children}
      </InfoCallout>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: compact ? "center" : undefined,
        borderRadius: 3,
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: (theme) => theme.palette.tokens?.[TONE_TOKENS[tone].border],
        bgcolor: (theme) => theme.palette.tokens?.[TONE_TOKENS[tone].bg],
        px: 2,
        py: 1,
        gap: 1,
      }}
    >
      {icon}
      {/* `div` y no `p`: los consumidores a veces pasan bloques (checkbox + texto, otro Typography) y un
          `<p>` dentro de otro `<p>` es HTML inválido. */}
      <Typography component="div" variant="body2" color="text.secondary">
        {children}
      </Typography>
    </Box>
  );
};
